import {
  type DrillProgress,
  type Stage,
  passesPerStage,
} from "../handwriting/xiezi-stages.js";

/**
 * The shape of the pies over a drill square: a word for each stage, a path
 * for each wedge, and how full each pie is where the reader is.
 * `xiezi-pies.ts` draws them.
 */

/** The word over each stage's pie. */
export const stageNames: Readonly<Record<Stage, string>> = {
  cued: "Follow",
  traced: "Trace",
  snapped: "Snap",
  free: "Recall",
};

/** A point on the unit circle, a fraction of the way round from the top. */
function around(fraction: number): string {
  const angle = fraction * 2 * Math.PI;

  return `${Math.sin(angle).toFixed(4)} ${(-Math.cos(angle)).toFixed(4)}`;
}

/** The path of one wedge of a pie cut into `of`, clockwise from the top. */
export function wedgePath(at: number, of: number): string {
  return `M0 0L${around(at / of)}A1 1 0 0 1 ${around((at + 1) / of)}Z`;
}

/** What one stage's pie looks like, where the reader is. */
export type PieState = "done" | "current" | "ahead";

/** How many wedges of a stage's pie are filled, and what state it is in. */
export function pieOf(
  progress: DrillProgress,
  stage: number,
): { readonly state: PieState; readonly filled: number } {
  if (stage < progress.stage) {
    return { state: "done", filled: passesPerStage };
  }

  return stage === progress.stage
    ? { state: "current", filled: progress.pass }
    : { state: "ahead", filled: 0 };
}
